import { animate, scroll } from 'motion';

// Own the chapter choreography in one place so the embedded About page does
// not run its standalone reveal controller over the same elements.
export function initHomeStoryMotion(root, onChapter) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const chapters = [...root.querySelectorAll('[data-story-chapter]')];
  const targets = [...root.querySelectorAll([
    '[data-story-reveal]',
    '.about-story h2',
    '.about-story [role=heading]',
    '.about-story p:not(.about-ai-path-copy)',
    '.about-story .photo-frame',
    '.about-story .research-with-image-img',
    '.about-story .about-route-card',
    '.about-story .home-route-portal',
    '.about-story .scroll-container',
    '.about-story .about-ai-path',
    '.about-story .home-ai-slot',
    '.about-story #videoElement',
  ].join(','))];
  const videos = [...root.querySelectorAll('video')];
  const visibleVideos = new Set();
  const cleanupScroll = [];
  let frame = 0;
  let active = '';
  let stopped = false;

  targets.forEach((el, index) => {
    el.classList.add('story-enter');
    el.style.setProperty('--story-delay', `${(index % 3) * 60}ms`);
  });
  const reveal = (el) => el.classList.add('is-story-visible');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      reveal(target);
      observer.unobserve(target);
    });
  }, { rootMargin: '0px 0px -5% 0px', threshold: 0.01 });
  targets.forEach(el => observer.observe(el));
  // Vue owns the root class binding and rewrites it as entrance/scene state changes.
  // Keep the scroll controller's readiness on its own attribute.
  root.dataset.storyMotion = 'ready';

  function updateChapter() {
    frame = 0;
    const line = window.innerHeight * 0.42;
    let next = chapters[0]?.id;
    for (const chapter of chapters) {
      if (chapter.getBoundingClientRect().top <= line) next = chapter.id;
    }
    if (next && next !== active) {
      active = next;
      onChapter(next);
    }
  }
  function queueChapter() {
    if (!frame) frame = requestAnimationFrame(updateChapter);
  }
  function canPlay(video) {
    return !reduced.matches
      && !document.hidden
      && video.dataset.userPaused !== 'true'
      && video.dataset.sceneInterlude !== 'true';
  }
  const mediaObserver = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) visibleVideos.add(target);
      else visibleVideos.delete(target);
      if (isIntersecting && canPlay(target)) target.play().catch(() => {});
      else target.pause();
    });
  }, { threshold: 0.15 });
  videos.forEach(video => mediaObserver.observe(video));

  function stopParallax() {
    cleanupScroll.splice(0).forEach(stop => stop());
  }
  function configureMotion() {
    stopParallax();
    if (reduced.matches) {
      targets.forEach(reveal);
      videos.forEach(video => video.pause());
      return;
    }
    root.querySelectorAll('.about-story .photo-frame img, .about-story .research-with-image-img').forEach(image => {
      const animation = animate(image, { transform: ['translateY(14px) scale(1.04)', 'translateY(-14px) scale(1.04)'] }, { ease: 'linear' });
      const stop = scroll(animation, { target: image.closest('[data-story-chapter]'), offset: ['start end', 'end start'] });
      cleanupScroll.push(() => { stop(); animation.cancel(); });
    });
  }
  function visibilityChanged() {
    videos.forEach(video => {
      if (visibleVideos.has(video) && canPlay(video)) video.play().catch(() => {});
      else video.pause();
    });
  }
  function focusReveal(event) {
    let target = event.target;
    while (target && target !== root) {
      if (target.classList?.contains('story-enter')) reveal(target);
      target = target.parentElement;
    }
  }
  window.addEventListener('scroll', queueChapter, { passive: true });
  window.addEventListener('resize', queueChapter, { passive: true });
  document.addEventListener('visibilitychange', visibilityChanged);
  reduced.addEventListener('change', configureMotion);
  root.addEventListener('focusin', focusReveal);
  configureMotion();
  updateChapter();
  return () => {
    if (stopped) return;
    stopped = true;
    observer.disconnect();
    mediaObserver.disconnect();
    stopParallax();
    cancelAnimationFrame(frame);
    window.removeEventListener('scroll', queueChapter);
    window.removeEventListener('resize', queueChapter);
    document.removeEventListener('visibilitychange', visibilityChanged);
    reduced.removeEventListener('change', configureMotion);
    root.removeEventListener('focusin', focusReveal);
    delete root.dataset.storyMotion;
    targets.forEach(el => {
      el.classList.remove('story-enter', 'is-story-visible');
      el.style.removeProperty('--story-delay');
    });
    videos.forEach(video => video.pause());
  };
}
